import { FC, useCallback, useState } from "react";
import { TestimonialSection } from "./component/TestimonialSection";
import { EnterpriseAuthContainer } from "./component/EnterpriseAuthContainer";
import { AuthHeader } from "./component/AuthHeader";
import { AuthAlternative } from "./component/AuthAlternative";
import { Link, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import {
  emptySigninFormValue,
  SigninAuthValues,
  signinSchema,
} from "./validation";
import InputField from "../../components/InputField";
import PasswordInput from "../../components/PasswordInput";
import Button from "../../components/Button";
import { SigninApi } from "../../API/auth";

export const SigninPage: FC = () => {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);

  const handleSignInButton = useCallback(
    async (data: SigninAuthValues) => {
      setIsLoading(true);

      SigninApi<{ token: string; role: string }>(data)
        .then((res) => {
          const { token, role } = res.data;
          const search = new URLSearchParams(location.search);
          const redirect = search.get("redirect");

          if (redirect) {
            window.location.replace(`${redirect}?&token=${token}&role=${role}`);
          } else {
            window.location.replace(
              `${process.env.REACT_APP_REDIRECT_URL}/workspace?&token=${token}&role=${role}`,
            );
          }
        })
        .catch(() => {
          setIsLoading(false);
        });
    },
    [location],
  );

  const {
    errors,
    handleChange,
    values,
    touched,
    handleSubmit,
    isValid,
    dirty,
  } = useFormik({
    initialValues: emptySigninFormValue,
    validationSchema: signinSchema,
    onSubmit: async (): Promise<void> => {
      await handleSignInButton(values);
    },
  });

  const isButtonDisabled = !isValid || !dirty;

  return (
    <div className="flex h-screen flex-col md:flex-row">
      <EnterpriseAuthContainer>
        <>
          <AuthHeader
            title="Sign In"
            subtitle="Create an account to get started"
          />
          <form className="" onSubmit={handleSubmit}>
            <InputField
              name="email"
              placeholder="Enter Email"
              id="Login_username"
              label="Email"
              error={(touched?.email && errors?.email) || ""}
              value={values.email}
              onChange={handleChange}
            />
            <PasswordInput
              name="password"
              label="Password"
              id="Login_password"
              placeholder="••••••••••••"
              error={(touched?.password && errors?.password) || ""}
              value={values.password}
              onChange={handleChange}
            />
            <Link
              to="/enterprise/forget-password"
              className="flex justify-end text-sm font-medium font-satoshi text-bblack-1 hover:cursor-pointer"
            >
              Forget Password
            </Link>
            <Button
              text="Sign In"
              className="w-full mt-6"
              type={isButtonDisabled ? "muted" : "primary"}
              disabled={isButtonDisabled}
              isLoading={isLoading}
            />
          </form>
          <AuthAlternative
            description="Don't have an account?"
            buttonText="Sign Up"
            buttonLink="/enterprise/signup"
          />
        </>
      </EnterpriseAuthContainer>

      <TestimonialSection />
    </div>
  );
};
